import { Helmet } from "react-helmet-async";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  HardHat, 
  CheckCircle2, 
  Clock,
  Phone,
  ArrowLeft,
  Users,
  Award,
  Eye,
  Hand,
  Footprints,
  Ear,
  ShieldCheck
} from "lucide-react";

const courseDetails = {
  duration: "4 שעות",
  certification: "תעודת השתתפות",
  participants: "עד 25 משתתפים",
  practical: "תרגול מעשי"
};

const ppeTypes = [
  {
    icon: HardHat,
    name: "הגנת ראש",
    items: ["קסדות", "כובעי מגן", "רשתות שיער"],
    when: "סכנת נפילת חפצים, חבטות, סביבות בנייה"
  },
  {
    icon: Eye,
    name: "הגנת עיניים ופנים",
    items: ["משקפי מגן", "מסכות פנים", "משקפי ריתוך"],
    when: "שבבים, התזות כימיות, קרינה"
  },
  {
    icon: Ear,
    name: "הגנת שמיעה",
    items: ["אטמי אוזניים", "אוזניות מגן"],
    when: "רעש מעל 85 דציבל, סביבות תעשייתיות"
  },
  {
    icon: Hand,
    name: "הגנת ידיים",
    items: ["כפפות עבודה", "כפפות כימיות", "כפפות נגד חתכים"],
    when: "עבודה עם חומרים, חדים, כימיקלים"
  },
  {
    icon: Footprints,
    name: "הגנת רגליים",
    items: ["נעלי בטיחות", "מגפי גומי", "כיסויי נעליים"],
    when: "סכנת מעיכה, התחשמלות, מים"
  },
  {
    icon: ShieldCheck,
    name: "הגנת גוף",
    items: ["אפודי זוהר", "סרבלים", "חגורות ורתמות"],
    when: "עבודה בגובה, נראות, חשיפות"
  }
];

const courseTopics = [
  "סוגי ציוד מגן וייעודם",
  "בחירת ציוד מתאים לסיכון",
  "לבישה נכונה והתאמה",
  "בדיקת תקינות לפני שימוש",
  "תחזוקה ואחסון נכון",
  "זיהוי סימני בלאי והחלפה",
  "תקנות ותקנים רלוונטיים",
  "אחריות המעסיק והעובד"
];

const legalRequirements = [
  {
    title: "חובת אספקה",
    description: "המעסיק חייב לספק ציוד מגן מתאים ללא עלות לעובד"
  },
  {
    title: "חובת הדרכה",
    description: "חובה להדריך עובדים בשימוש נכון בציוד המגן"
  },
  {
    title: "חובת שימוש",
    description: "העובד חייב להשתמש בציוד המגן שהוקצה לו"
  },
  {
    title: "תחזוקה",
    description: "חובה לתחזק את הציוד ולהחליף במידת הצורך"
  }
];

const faqs = [
  {
    question: "מי אחראי על אספקת ציוד מגן אישי?",
    answer: "המעסיק אחראי לספק ציוד מגן אישי מתאים לכל עובד, ללא עלות. המעסיק גם אחראי על תחזוקה והחלפה של ציוד בלוי."
  },
  {
    question: "האם ההדרכה כוללת תרגול מעשי?",
    answer: "כן, ההדרכה כוללת תרגול מעשי של לבישת הציוד, בדיקת תקינות והתאמה אישית. כל משתתף מתרגל עם סוגי הציוד הרלוונטיים לעבודתו."
  },
  {
    question: "מה קורה אם עובד מסרב להשתמש בציוד מגן?",
    answer: "עובד שמסרב להשתמש בציוד מגן מפר את החוק. המעסיק רשאי לנקוט צעדים משמעתיים. חשוב להבין ולהסביר לעובדים את חשיבות הציוד."
  },
  {
    question: "כמה פעמים צריך להחליף ציוד מגן?",
    answer: "תדירות ההחלפה תלויה בסוג הציוד והשימוש. יש לבדוק את הציוד לפני כל שימוש ולהחליף כשמזהים סימני בלאי, נזק או לפי הנחיות היצרן."
  }
];

export default function PPE() {
  return (
    <Layout>
      <Helmet>
        <title>הדרכת ציוד מגן אישי (PPE) | Adam Safety</title>
        <meta name="description" content="הדרכת ציוד מגן אישי מקצועית - בחירה, לבישה, תחזוקה ובדיקת ציוד מגן. קסדות, משקפיים, כפפות, נעלי בטיחות ועוד. תרגול מעשי." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          style={{ background: 'var(--gradient-hero)' }}
        >
          <div className="absolute inset-0 pattern-hero opacity-30" />
        </div>

        <div className="container relative z-10 px-4 py-20 md:py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/20 text-accent mb-6">
              <HardHat className="w-5 h-5" />
              <span className="text-sm font-medium">הדרכת בטיחות</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground mb-6">
              הדרכת ציוד מגן אישי
            </h1>
            
            <p className="text-xl md:text-2xl text-primary-foreground/80 mb-8 max-w-3xl mx-auto leading-relaxed">
              למדו לבחור, ללבוש ולתחזק ציוד מגן אישי נכון - 
              השקעה קטנה שמצילה חיים
            </p>

            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <div className="flex items-center gap-2 bg-primary-foreground/10 px-4 py-2 rounded-full">
                <Clock className="w-4 h-4 text-accent" />
                <span className="text-primary-foreground text-sm">{courseDetails.duration}</span>
              </div>
              <div className="flex items-center gap-2 bg-primary-foreground/10 px-4 py-2 rounded-full">
                <Award className="w-4 h-4 text-accent" />
                <span className="text-primary-foreground text-sm">{courseDetails.certification}</span>
              </div>
              <div className="flex items-center gap-2 bg-primary-foreground/10 px-4 py-2 rounded-full">
                <Users className="w-4 h-4 text-accent" />
                <span className="text-primary-foreground text-sm">{courseDetails.participants}</span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button variant="cta" size="lg" className="gap-2 text-lg px-8">
                  <Phone className="w-5 h-5" />
                  הזמן הדרכה
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* PPE Types */}
      <section className="section-padding bg-background">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              סוגי ציוד מגן
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              הכירו את סוגי הציוד השונים ומתי נדרש להשתמש בהם
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {ppeTypes.map((ppe, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-service"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="icon-container">
                    <ppe.icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground">{ppe.name}</h3>
                </div>
                <div className="mb-3">
                  <p className="text-xs text-accent font-medium mb-1">כולל:</p>
                  <p className="text-sm text-muted-foreground">{ppe.items.join(", ")}</p>
                </div>
                <div>
                  <p className="text-xs text-accent font-medium mb-1">מתי נדרש:</p>
                  <p className="text-sm text-muted-foreground">{ppe.when}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Course Topics */}
      <section className="section-padding section-alt">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              נושאי ההדרכה
            </h2>
          </motion.div>

          <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
            {courseTopics.map((topic, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="flex items-center gap-2 bg-card px-4 py-2 rounded-full border border-border shadow-sm"
              >
                <CheckCircle2 className="w-4 h-4 text-accent" />
                <span className="text-foreground text-sm">{topic}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Legal Requirements */}
      <section className="section-padding bg-background">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              דרישות החוק
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {legalRequirements.map((req, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-primary rounded-2xl p-6 text-primary-foreground"
              >
                <ShieldCheck className="w-8 h-8 text-accent mb-3" />
                <h3 className="font-bold mb-2">{req.title}</h3>
                <p className="text-primary-foreground/80 text-sm">{req.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding section-alt">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              שאלות נפוצות
            </h2>
          </motion.div>

          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-glass p-6"
              >
                <h3 className="text-lg font-bold text-foreground mb-2">{faq.question}</h3>
                <p className="text-muted-foreground">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-primary">
        <div className="container px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-primary-foreground mb-4">
              הגנו על העובדים שלכם
            </h2>
            <p className="text-xl text-primary-foreground/80 mb-8">
              הדרכה נכונה על ציוד מגן = פחות תאונות
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button variant="cta" size="lg" className="gap-2 text-lg px-8">
                  <Phone className="w-5 h-5" />
                  הזמנת הדרכה
                </Button>
              </Link>
              <Link to="/trainings">
                <Button variant="outline" size="lg" className="gap-2 text-lg px-8 border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10">
                  הדרכות נוספות
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}